import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Heart, 
  Book, 
  Headphones, 
  Clock, 
  ExternalLink,
  Brain,
  Timer,
  PlayCircle,
  PauseCircle,
  Waves,
  Users,
  BookOpen,
  Wind,
  Sparkles,
  HeartPulse
} from 'lucide-react';
import { toast } from 'sonner';

const WellnessResources = () => {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('journaling');
  
  // Meditation Timer State
  const [meditationTime, setMeditationTime] = useState(5); // minutes
  const [meditationActive, setMeditationActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // seconds
  
  // Breathing Exercise State  
  const [breathingActive, setBreathingActive] = useState(false);
  const [breathPhase, setBreathPhase] = useState('inhale'); // 'inhale', 'hold', 'exhale'
  const [breathCount, setBreathCount] = useState(0);

  useEffect(() => {
    fetchWellnessData();
  }, []);

  // Meditation Timer Logic
  useEffect(() => {
    let interval;
    if (meditationActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setMeditationActive(false);
            return meditationTime * 60;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [meditationActive, timeRemaining, meditationTime]);

  // Breathing Exercise Logic
  useEffect(() => {
    let interval;
    if (breathingActive) {
      interval = setInterval(() => {
        setBreathCount(prev => {
          const newCount = prev + 1;
          const cycle = newCount % 12; // 4s inhale + 4s hold + 4s exhale
          
          if (cycle <= 4) {
            setBreathPhase('inhale');
          } else if (cycle <= 8) {
            setBreathPhase('hold');
          } else {
            setBreathPhase('exhale');
          }
          
          return newCount;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [breathingActive]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchWellnessData = async () => {
    try {
      const response = await axios.get('/wellness/resources');
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching wellness data:', error);
      toast.error('Failed to load wellness resources');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'journaling', name: 'Journaling', icon: BookOpen, gradient: 'from-purple-500 to-pink-500', count: resources?.journaling_prompts?.length || 0 },
    { id: 'meditation', name: 'Meditation & Breathing', icon: Wind, gradient: 'from-blue-500 to-cyan-500', count: 'Tools' },
    { id: 'mindfulness', name: 'Mindfulness', icon: Sparkles, gradient: 'from-emerald-500 to-green-500', count: resources?.mindfulness?.length || 0 },
    { id: 'self-care', name: 'Self-Care', icon: HeartPulse, gradient: 'from-pink-500 to-rose-500', count: resources?.self_care_activities?.length || 0 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="wellness-loading">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading wellness resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 pb-12" data-testid="wellness-resources">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-4">Wellness Resources</h1>
          <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
            Discover tools, exercises, and resources to support your mental health and wellness journey.
          </p>
        </div>

        {/* Crisis support removed */}

        {/* Tabs Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative overflow-hidden rounded-2xl p-6 transition-all shadow-lg hover:shadow-xl hover:scale-105 ${
                  isActive
                    ? `bg-gradient-to-br ${tab.gradient} text-white scale-[1.02]`
                    : 'bg-white text-emerald-700 hover:bg-emerald-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                )}
                <div className="flex flex-col items-center space-y-3 text-center">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                    isActive ? 'bg-white/20' : `bg-gradient-to-br ${tab.gradient}`
                  }`}>
                    <IconComponent className={`w-8 h-8 ${isActive ? 'text-white' : 'text-white'}`} strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className={`font-semibold text-lg ${isActive ? 'text-white' : 'text-emerald-800'}`}>
                      {tab.name}
                    </h3>
                    <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                      isActive ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-600'
                    }`}>
                      {tab.count}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Content Sections */}
        <div className="animate-fade-in-up">
          {/* Meditation & Breathing Tab */}
          {activeTab === 'meditation' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-emerald-800 mb-3">Meditation & Breathing Tools</h2>
                <p className="text-emerald-600 max-w-2xl mx-auto">
                  Use these guided tools for meditation practice and breathing exercises to reduce anxiety and stress.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Meditation Timer */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8">
                  <div className="text-center space-y-8">
                    <div className="relative">
                      <div className={`w-40 h-40 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center transition-all duration-1000 ${
                        meditationActive ? 'animate-pulse' : ''
                      }`}>
                        <div className="text-white">
                          <div className="text-3xl font-bold">{formatTime(timeRemaining)}</div>
                          <div className="text-sm opacity-80">remaining</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-emerald-800">Meditation Timer</h3>
                      
                      <div className="flex items-center justify-center space-x-4">
                        <label className="text-emerald-700">Duration:</label>
                        <select
                          value={meditationTime}
                          onChange={(e) => {
                            const time = parseInt(e.target.value);
                            setMeditationTime(time);
                            if (!meditationActive) {
                              setTimeRemaining(time * 60);
                            }
                          }}
                          className="px-3 py-1 border border-emerald-300 rounded-lg"
                          disabled={meditationActive}
                        >
                          <option value={1}>1 minute</option>
                          <option value={3}>3 minutes</option>
                          <option value={5}>5 minutes</option>
                          <option value={10}>10 minutes</option>
                          <option value={15}>15 minutes</option>
                          <option value={20}>20 minutes</option>
                        </select>
                      </div>
                      
                      <button
                        onClick={() => {
                          if (meditationActive) {
                            setMeditationActive(false);
                            setTimeRemaining(meditationTime * 60);
                          } else {
                            setMeditationActive(true);
                          }
                        }}
                        className="btn-primary flex items-center space-x-2 mx-auto"
                      >
                        {meditationActive ? <PauseCircle className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
                        <span>{meditationActive ? 'Stop' : 'Start'} Meditation</span>
                      </button>
                      
                      {meditationActive && (
                        <div className="space-y-2">
                          <p className="text-emerald-600">Close your eyes and focus on your breath...</p>
                          <p className="text-sm text-emerald-500">Let thoughts come and go without judgment</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Breathing Exercise */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8">
                  <div className="text-center space-y-8">
                    <div className="relative">
                      <div 
                        className={`w-32 h-32 mx-auto rounded-full border-4 border-emerald-500 transition-all duration-1000 ${
                          breathPhase === 'inhale' ? 'scale-125 bg-emerald-100' : 
                          breathPhase === 'hold' ? 'scale-125 bg-emerald-200' : 
                          'scale-75 bg-emerald-50'
                        }`}
                      >
                        <div className="flex items-center justify-center h-full">
                          <Waves className={`w-12 h-12 text-emerald-600 transition-all duration-1000 ${
                            breathPhase === 'hold' ? 'scale-125' : ''
                          }`} />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-emerald-800">Breathing Exercise</h3>
                      <h4 className="text-lg text-emerald-700">
                        {breathPhase === 'inhale' && 'Breathe In...'}
                        {breathPhase === 'hold' && 'Hold...'}
                        {breathPhase === 'exhale' && 'Breathe Out...'}
                      </h4>
                      <p className="text-emerald-600">
                        {breathPhase === 'inhale' && 'Slowly inhale through your nose for 4 seconds'}
                        {breathPhase === 'hold' && 'Hold your breath gently for 4 seconds'}
                        {breathPhase === 'exhale' && 'Exhale slowly through your mouth for 4 seconds'}
                      </p>
                      
                      <button
                        onClick={() => setBreathingActive(!breathingActive)}
                        className="btn-primary flex items-center space-x-2 mx-auto"
                      >
                        {breathingActive ? <PauseCircle className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
                        <span>{breathingActive ? 'Pause' : 'Start'} Breathing Exercise</span>
                      </button>
                      
                      {breathingActive && (
                        <p className="text-sm text-emerald-500">
                          Cycles completed: {Math.floor(breathCount / 12)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Meditation Tips */}
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-emerald-800 mb-4">💡 Meditation & Breathing Tips</h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm text-emerald-700">
                  <div className="flex items-start space-x-2">
                    <Timer className="w-4 h-4 mt-0.5 text-emerald-500" />
                    <p>Start with shorter sessions (1-5 minutes) and gradually increase duration</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Waves className="w-4 h-4 mt-0.5 text-emerald-500" />
                    <p>Use breathing exercises during stressful moments throughout the day</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Heart className="w-4 h-4 mt-0.5 text-emerald-500" />
                    <p>Find a quiet, comfortable space and maintain consistent daily practice</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mindfulness Tab */}
          {activeTab === 'mindfulness' && resources?.mindfulness && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <Brain className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-emerald-800 mb-2">Mindfulness & Meditation</h2>
                <p className="text-emerald-600 max-w-2xl mx-auto">
                  Practice mindfulness to reduce stress, improve focus, and enhance emotional well-being.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resources.mindfulness.map((item, index) => (
                  <div key={index} className={`resource-card animate-fade-in-up stagger-${(index % 3) + 1}`} data-testid={`mindfulness-${index}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                        {item.type === 'guided_meditation' ? (
                          <Headphones className="w-6 h-6 text-white" />
                        ) : (
                          <Brain className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <div className="flex items-center space-x-1 text-amber-600">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{item.duration}</span>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-emerald-800 mb-2">{item.title}</h3>
                    <p className="text-emerald-700 text-sm mb-4">{item.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full capitalize">
                        {item.type.replace('_', ' ')}
                      </span>
                      <button className="text-emerald-600 hover:text-emerald-700 flex items-center space-x-1">
                        <span className="text-sm font-medium">Start</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Journaling Tab */}
          {activeTab === 'journaling' && resources?.journaling_prompts && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <Book className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-emerald-800 mb-2">Journaling Prompts</h2>
                <p className="text-emerald-600 max-w-2xl mx-auto">
                  Explore your thoughts and emotions through guided journaling exercises.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resources.journaling_prompts.map((prompt, index) => (
                  <div key={index} className={`card animate-fade-in-up stagger-${(index % 2) + 1}`} data-testid={`journal-prompt-${index}`}>
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-300 to-green-400 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-emerald-800 font-medium leading-relaxed">{prompt}</p>
                        <button className="mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center space-x-1">
                          <span>Start Writing</span>
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Self-Care Tab */}
          {activeTab === 'self-care' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <Heart className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-emerald-800 mb-2">Self-Care Activities</h2>
                <p className="text-emerald-600 max-w-2xl mx-auto">
                  Nurture your well-being with these self-care practices and activities organized by category.
                </p>
              </div>
              
              {/* Physical Self-Care */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-lg flex items-center justify-center">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-emerald-800">Physical Self-Care</h3>
                    <p className="text-sm text-emerald-600">Activities to nurture your body and physical health</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <a href="/relax" className="card hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🛁</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Take a Warm Bath</h4>
                    <p className="text-sm text-emerald-600 mb-3">To enjoy an Epsom salt bath, ensure that your tub is adequately heated with warm water, add a minimum of 1 to 2 cups of Epsom salts, and gently stir to dissolve. It is advisable to allow for at least 15–20 minutes of soakage to alleviate sore muscles, alleviate stress, and enhance restful sleep. Before entering the water, it is advisable to consult a physician if you are experiencing conditions such as low blood pressure, wounds, or are pregnant, as hot baths can pose a potential risk.</p>
                    <span className="text-emerald-600 text-sm font-medium group-hover:text-emerald-700">Explore relaxation tools →</span>
                  </a>
                  
                  <a href="/mood" className="card hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🚶</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Go for a Walk in Nature</h4>
                    <p className="text-sm text-emerald-600 mb-3">A regular stroll in nature can significantly decrease anxiety and stress. Natural surroundings assist in lowering cortisol levels, the primary stress hormone, and enhance the synthesis of mood-elevating neurotransmitters such as serotonin, dopamine, and endorphins. Even a brief exposure to the outdoors can enhance your mood and overall health.</p>
                    <span className="text-emerald-600 text-sm font-medium group-hover:text-emerald-700">Track your mood →</span>
                  </a>
                  
                  <div className="card" onClick={() => setActiveTab('meditation')}>
                    <div className="flex items-start justify-between mb-3 cursor-pointer">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">💆</span>
                      </div>
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Progressive Muscle Relaxation</h4>
                    <p className="text-sm text-emerald-600 mb-3">Release physical tension with guided relaxation techniques</p>
                    <button className="text-emerald-600 text-sm font-medium hover:text-emerald-700">Try breathing exercises →</button>
                  </div>
                </div>
              </div>

              {/* Social Self-Care */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-emerald-800">Social Self-Care</h3>
                    <p className="text-sm text-emerald-600">Connect with others and build meaningful relationships</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a href="/groups" className="card hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">📞</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Call a Friend or Family Member</h4>
                    <p className="text-sm text-emerald-600 mb-3">Calling a friend or family member to call is a commendable method of obtaining assistance, and you may enhance the conversation by making it more precise with your needs, whether it's someone to listen to or someone to offer guidance. If you need immediate crisis support in Australia, consider contacting a helpline like Lifeline on 13 11 14. For crises, it is advisable to dial triple zero 000.</p>
                    <span className="text-emerald-600 text-sm font-medium group-hover:text-emerald-700">Join support groups →</span>
                  </a>
                  
                  <a href="/matches" className="card hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">💬</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Connect with Your Matches</h4>
                    <p className="text-sm text-emerald-600 mb-3">Share experiences with people who understand your wellness journey</p>
                    <span className="text-emerald-600 text-sm font-medium group-hover:text-emerald-700">View matches →</span>
                  </a>
                </div>
              </div>

              {/* Emotional Self-Care */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-emerald-800">Emotional Self-Care</h3>
                    <p className="text-sm text-emerald-600">Process emotions and practice mindfulness</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="card cursor-pointer" onClick={() => setActiveTab('journaling')}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">📔</span>
                      </div>
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Write in a Gratitude Journal</h4>
                    <p className="text-sm text-emerald-600 mb-3">Focus on positive aspects and cultivate appreciation</p>
                    <button className="text-emerald-600 text-sm font-medium hover:text-emerald-700">Start journaling →</button>
                  </div>
                  
                  <a href="/relax" className="card hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🎵</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Listen to Your Favorite Music</h4>
                    <p className="text-sm text-emerald-600 mb-3">Use music to improve mood and emotional regulation</p>
                    <span className="text-emerald-600 text-sm font-medium group-hover:text-emerald-700">Explore relaxation →</span>
                  </a>
                  
                  <div className="card cursor-pointer" onClick={() => setActiveTab('mindfulness')}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🧘</span>
                      </div>
                    </div>
                    <h4 className="text-emerald-800 font-semibold mb-2">Practice Mindfulness</h4>
                    <p className="text-sm text-emerald-600 mb-3">Stay present and reduce anxiety with mindfulness exercises</p>
                    <button className="text-emerald-600 text-sm font-medium hover:text-emerald-700">Try mindfulness →</button>
                  </div>
                </div>
              </div>

              {/* Tips Section */}
              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
                <h3 className="text-lg font-semibold text-emerald-800 mb-4">💡 Self-Care Tips</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-emerald-700">
                  <div className="flex items-start space-x-2">
                    <Heart className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                    <p>Self-care isn't selfish - it's essential for maintaining your mental and physical health</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Clock className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                    <p>Schedule self-care activities just like any other important appointment</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Brain className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                    <p>Start small - even 5-10 minutes of self-care can make a significant difference</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Users className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                    <p>Mix different types of self-care activities to address all aspects of your well-being</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Crisis support section removed */}
        </div>
      </div>
    </div>
  );
};

export default WellnessResources;